import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type DealViewingsResult, type Viewing } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Site visits and viewings (G-088), on the deal and the lead.
 *
 * Book a slot, then say what happened. A clash is refused by the server with
 * the other booking named, and shown here as it was said. A viewing that has
 * started asks for its outcome; one still ahead can only be moved or cancelled.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';
const INTEREST = ['hot', 'warm', 'cold', 'not-interested'] as const;

/** A datetime-local value, read in the viewer's own time, as an ISO instant. */
const toInstant = (local: string): string => new Date(local).toISOString();

export function ViewingsPanel({ objectKey, recordId }: { objectKey: string; recordId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-141').enabled;
  const applies = enabled && (objectKey === 'deal' || objectKey === 'lead');
  const key = ['viewings', objectKey, recordId];

  const list = useQuery<DealViewingsResult, ApiError>({
    queryKey: key,
    queryFn: () =>
      objectKey === 'deal'
        ? api.dealViewings({ path: { dealId: recordId } })
        : api.leadViewings({ path: { leadId: recordId } }),
    enabled: applies,
  });

  const [draft, setDraft] = useState({ startsAt: '', endsAt: '', location: '' });
  const [open, setOpen] = useState<string | null>(null);
  const [outcome, setOutcome] = useState({ interest: '', note: '' });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });

  const book = useMutation({
    mutationFn: () => {
      const body = {
        startsAt: toInstant(draft.startsAt),
        endsAt: toInstant(draft.endsAt),
        location: draft.location.trim() || null,
      };
      return objectKey === 'deal'
        ? api.bookDealViewing({ path: { dealId: recordId }, body })
        : api.bookLeadViewing({ path: { leadId: recordId }, body });
    },
    onSuccess: () => {
      setDraft({ startsAt: '', endsAt: '', location: '' });
      refresh();
    },
  });

  const record = useMutation({
    mutationFn: (input: { visitId: string; outcome: 'held' | 'no-show' | 'cancelled' }) =>
      api.recordViewingOutcome({
        path: { visitId: input.visitId },
        body: {
          outcome: input.outcome,
          note: outcome.note.trim() || null,
          interest: input.outcome === 'held' && outcome.interest ? (outcome.interest as Viewing['interest']) : null,
        },
      }),
    onSuccess: () => {
      setOpen(null);
      setOutcome({ interest: '', note: '' });
      refresh();
    },
  });

  if (!applies || list.isPending) return null;
  if (list.isError) {
    return (
      <Panel data-region="viewings">
        <p className="text-dense text-danger-text">{errorText(list.error)}</p>
      </Panel>
    );
  }

  const now = Date.now();
  const when = (viewing: Viewing) =>
    viewing.startsAt ? new Date(viewing.startsAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

  return (
    <Panel data-region="viewings">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('viewings.title')}</SectionLabel>

        {list.data.viewings.length === 0 ? (
          <p className="text-meta text-ink-meta">{t('viewings.none')}</p>
        ) : (
          <ul className="flex flex-col gap-2" data-viewings>
            {list.data.viewings.map((viewing) => {
              const started = viewing.startsAt !== null && new Date(viewing.startsAt).getTime() <= now;
              const awaiting = viewing.status === 'planned' || viewing.status === 'arrived';
              return (
                <li key={viewing.visitId} data-viewing={viewing.visitId} data-viewing-status={viewing.status}
                  className="flex flex-col gap-1 text-dense text-ink">
                  <div className="flex flex-wrap gap-2">
                    <span>{when(viewing)}</span>
                    {viewing.location ? <span className="text-ink-600">{viewing.location}</span> : null}
                    <span className="text-ink-meta">{t(`viewings.status.${viewing.status}`)}</span>
                    {viewing.interest ? (
                      <span className="text-ink-meta" data-viewing-interest>{t(`viewings.interest.${viewing.interest}`)}</span>
                    ) : null}
                  </div>
                  {viewing.outcomeNote ? <p className="text-meta text-ink-600">{viewing.outcomeNote}</p> : null}
                  {awaiting && open !== viewing.visitId ? (
                    <div>
                      <Button size="small" data-viewing-outcome-open={viewing.visitId} onClick={() => setOpen(viewing.visitId)}>
                        {started ? t('viewings.outcome') : t('viewings.cancel')}
                      </Button>
                    </div>
                  ) : null}
                  {awaiting && open === viewing.visitId ? (
                    <div className="flex flex-col gap-2" data-viewing-outcome-form>
                      {started ? (
                        <label className="flex flex-col gap-1 text-meta text-ink-meta">
                          {t('viewings.interest')}
                          <select className={INPUT} data-viewing-interest-select value={outcome.interest}
                            onChange={(event) => setOutcome({ ...outcome, interest: event.target.value })}>
                            <option value="">—</option>
                            {INTEREST.map((level) => (
                              <option key={level} value={level}>{t(`viewings.interest.${level}`)}</option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                      <label className="flex flex-col gap-1 text-meta text-ink-meta">
                        {t('viewings.note')}
                        <textarea className="rounded-control border border-control bg-surface p-2 text-body text-ink"
                          data-viewing-note value={outcome.note}
                          onChange={(event) => setOutcome({ ...outcome, note: event.target.value })} />
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {started ? (
                          <>
                            <Button size="small" intent="primary" data-viewing-held
                              onClick={() => record.mutate({ visitId: viewing.visitId, outcome: 'held' })}>
                              {t('viewings.held')}
                            </Button>
                            <Button size="small" data-viewing-no-show
                              onClick={() => record.mutate({ visitId: viewing.visitId, outcome: 'no-show' })}>
                              {t('viewings.noShow')}
                            </Button>
                          </>
                        ) : null}
                        <Button size="small" data-viewing-cancel
                          onClick={() => record.mutate({ visitId: viewing.visitId, outcome: 'cancelled' })}>
                          {t('viewings.cancel')}
                        </Button>
                      </div>
                      {record.error ? <p className="text-dense text-danger-text">{errorText(record.error as ApiError)}</p> : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {list.data.rescheduled > 0 ? (
          <p className="text-meta text-ink-meta" data-viewings-moved>
            {t('viewings.moved', { values: { count: String(list.data.rescheduled) } })}
          </p>
        ) : null}

        <form data-viewing-book className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            book.mutate();
          }}>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('viewings.starts')}
            <input className={INPUT} type="datetime-local" required data-viewing-starts value={draft.startsAt}
              onChange={(event) => setDraft({ ...draft, startsAt: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('viewings.ends')}
            <input className={INPUT} type="datetime-local" required data-viewing-ends value={draft.endsAt}
              onChange={(event) => setDraft({ ...draft, endsAt: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('viewings.location')}
            <input className={INPUT} maxLength={300} data-viewing-location value={draft.location}
              onChange={(event) => setDraft({ ...draft, location: event.target.value })} />
          </label>
          {book.error ? <p className="text-dense text-danger-text" data-viewing-book-error>{errorText(book.error as ApiError)}</p> : null}
          <div>
            <Button intent="primary" size="small" type="submit" data-viewing-book-submit disabled={book.isPending}>
              {t('viewings.book')}
            </Button>
          </div>
        </form>
      </div>
    </Panel>
  );
}
